import { Form, Formik, FormikProps, FormikValues } from 'formik';
import { Moment } from 'moment';
import React, { useEffect, useMemo } from 'react';
import styled from '../../theme';
import { themeColor } from '../../theme/helpers';
import { Color } from '../../theme/primaryTheme';
import { formatMomentToIsoString, momentUtc } from '../../utilities/moment';
import Button from '../Button';
import DatePickerField from '../Field/DatePickerField';

interface Props {
  onDateChange?: (startDate?: string, endDate?: string) => void;
  initialStartDate?: string;
  initialEndDate?: string;
}

interface Values extends FormikValues {
  startDate: Moment | null;
  endDate: Moment | null;
}

const DATE_PLACEHOLDER = 'MM/DD/YYYY';

const createInitialValues = (startDateStr?: string, endDateStr?: string): Values => {
  return {
    startDate: startDateStr ? momentUtc(startDateStr) : null,
    endDate: endDateStr ? momentUtc(endDateStr) : null,
  };
};

const BulkLearnerActivityUploadDateFilter: React.FunctionComponent<Props> = ({ onDateChange, initialStartDate, initialEndDate }) => {
  const initialValues = useMemo<Values>(() => createInitialValues(initialStartDate, initialEndDate), [initialStartDate, initialEndDate]);

  return (
    <Formik
      enableReinitialize={true}
      initialValues={initialValues}
      validateOnChange={false}
      validateOnBlur={false}
      onSubmit={() => undefined}
    >
      {(formikProps: FormikProps<Values>) => {
        const { values } = formikProps;

        return <FormContent values={values} onDateChange={onDateChange} formikProps={formikProps} />;
      }}
    </Formik>
  );
};

interface FormContentProps {
  values: Values;
  onDateChange?: (startDate?: string, endDate?: string) => void;
  formikProps: FormikProps<Values>;
}

const FormContent: React.FunctionComponent<FormContentProps> = ({ values, onDateChange }) => {
  const hasInitialized = React.useRef(false);
  const { startDate, endDate } = values;

  useEffect(() => {
    if (!hasInitialized.current && startDate && endDate) {
      hasInitialized.current = true;
      const startDateStr = formatMomentToIsoString(startDate);
      const endDateStr = formatMomentToIsoString(endDate);
      if (onDateChange && startDateStr && endDateStr) {
        onDateChange(startDateStr, endDateStr);
      }
    }
  }, [startDate, endDate, onDateChange]);

  const handleSearch = () => {
    if (startDate && endDate) {
      const startDateStr = formatMomentToIsoString(startDate);
      const endDateStr = formatMomentToIsoString(endDate);
      if (onDateChange && startDateStr && endDateStr) {
        onDateChange(startDateStr, endDateStr);
      }
    }
  };

  const isSearchDisabled = !startDate || !endDate;

  return (
    <Form data-test-id="bulkLearnerActivityUploadDateFilter">
      <FilterRow>
        <FilterColumn>
          <DatePickerField name="startDate" label="Start Date" placeholder={DATE_PLACEHOLDER} required={true} />
        </FilterColumn>
        <FilterColumn>
          <DatePickerField name="endDate" label="End Date" placeholder={DATE_PLACEHOLDER} required={true} />
        </FilterColumn>
        <FilterButtonColumn>
          <SearchButton type="button" onClick={handleSearch} aria-disabled={isSearchDisabled} tabIndex={0}>
            Search
          </SearchButton>
        </FilterButtonColumn>
      </FilterRow>
    </Form>
  );
};

const FilterRow = styled.div`
  display: flex;
  align-items: flex-end;
  gap: 2rem;
  flex-wrap: wrap;
  margin-top: 2rem;
`;

const FilterColumn = styled.div`
  flex: 0 0 auto;
  min-width: 18rem;
  max-width: 22rem;
`;

const FilterButtonColumn = styled.div`
  display: flex;
  align-items: center;
  padding-bottom: 0.3rem; /* Adjust to align with input baseline */
  margin-left: auto;
`;

const SearchButton = styled(Button)`
  min-width: 10rem;
  height: 5.5rem;

  &[aria-disabled='true'] {
    background-color: ${themeColor(Color.Secondary)};
    opacity: 0.5;
    cursor: not-allowed;
  }
`;

export default BulkLearnerActivityUploadDateFilter;
